import { defineStore } from 'pinia';
import { reactive, toRefs } from 'vue';

import { fetchGeoLocationsByIds } from '@/api/geoService';
import { getPriceValue } from '@/api/iaziApi';
import { API, fetchPropertyData, getMyProperties } from '@/api/propertyValuation';
import { PriceValueResponse } from '@/models/queries/getPriceValue';
import { PropertyValuationResult } from '@/models/queries/getPropertyData';
import { Pages, routeLocationToGeoIdMapping } from '@/types';
import { captureAction } from '@smg-real-estate/browser-rum';
import { GeoLocation } from '@smg-real-estate/geo-libs';

export interface CommonState {
  currentPage: Pages | null;
  isResolvingLocations: boolean;
  routeGeoLocations: GeoLocation[];
  routeLocationsPrice: PriceValueResponse[];
  isUserFirstTimeLogin: boolean;
  loggedIn: boolean;
  username: string;
  userProperties: PropertyValuationResult[] | null;
  canUserCreate: boolean;
}

export const getDefaultState = (): CommonState => ({
  currentPage: null,
  isResolvingLocations: true,
  routeGeoLocations: [],
  routeLocationsPrice: [],
  isUserFirstTimeLogin: false,
  loggedIn: false,
  username: '',
  userProperties: null,
  canUserCreate: true,
});

export const useCommonStore = defineStore('common', () => {
  const state = reactive<CommonState>(getDefaultState());

  const setCurrentPage = (value: Pages | null): void => {
    state.currentPage = value;
  };

  const setIsResolvingLocations = (value: boolean): void => {
    state.isResolvingLocations = value;
  };

  const setRouteGeoLocations = (value: GeoLocation[]): void => {
    state.routeGeoLocations = value;
  };

  const setRouteLocationsPrice = (value: PriceValueResponse[]): void => {
    state.routeLocationsPrice = value;
  };

  const setIsUserFirstTimeLogin = (value: boolean): void => {
    state.isUserFirstTimeLogin = value;
  };

  const setLoggedInUser = (value: string): void => {
    state.loggedIn = true;
    state.username = value;
  };

  const resetUser = (): void => {
    state.loggedIn = false;
    state.username = '';
    state.userProperties = null;
    state.canUserCreate = true;
  };

  const setUserProperties = (value: PropertyValuationResult[] | null): void => {
    state.userProperties = value;
  };

  const setCanUserCreate = (value: boolean): void => {
    state.canUserCreate = value;
  };

  const fetchPriceDataForRouteLocations = async (): Promise<void> => {
    if (!state.routeGeoLocations.length) {
      return;
    }

    const result: Array<PriceValueResponse | undefined> = await Promise.all(
      state.routeGeoLocations.map((geoLocation) => {
        const level = geoLocation.iazi?.level;
        const levelId = geoLocation.iazi?.id;
        if (!level || !levelId) {
          captureAction(`No iazi level and id found for ${geoLocation.id}`, {
            geoId: geoLocation.id,
          });
          return;
        }
        return getPriceValue({ level, levelId });
      }),
    );

    setRouteLocationsPrice(
      result.filter((priceData): priceData is PriceValueResponse => Boolean(priceData)),
    );
  };

  const resolveRouteGeoLocations = async (): Promise<void> => {
    const routeLocationIds = Object.values(routeLocationToGeoIdMapping);
    setIsResolvingLocations(true);
    const geoLocations = await fetchGeoLocationsByIds(routeLocationIds);
    setRouteGeoLocations(geoLocations);
    await fetchPriceDataForRouteLocations();
    setIsResolvingLocations(false);
  };

  const fetchUserProperties = async (
    forceRefresh = false,
  ): Promise<PropertyValuationResult[] | null> => {
    if (state.userProperties && !forceRefresh) {
      return state.userProperties;
    }

    const response = await getMyProperties();
    const properties = response?.data || null;

    setUserProperties(properties);

    if (response) {
      setCanUserCreate(response.canUserCreate ?? true);
    }

    return properties;
  };

  const fetchUserProperty = async (propertyId: string): Promise<PropertyValuationResult | null> => {
    if (state.userProperties) {
      const cachedProperty = state.userProperties.find(
        (property) => property.leadId === propertyId,
      );
      if (cachedProperty) {
        return cachedProperty;
      }
    }

    try {
      const propertyData = await fetchPropertyData(propertyId, 4, API.HomeOwnerWorld);
      return propertyData ? (propertyData as PropertyValuationResult) : null;
    } catch (error) {
      captureAction('Failed to fetch user property', { propertyId, error });
      return null;
    }
  };

  return {
    ...toRefs(state),
    setCurrentPage,
    setIsResolvingLocations,
    setRouteGeoLocations,
    setRouteLocationsPrice,
    setIsUserFirstTimeLogin,
    setLoggedInUser,
    resetUser,
    setUserProperties,
    setCanUserCreate,
    resolveRouteGeoLocations,
    fetchPriceDataForRouteLocations,
    fetchUserProperties,
    fetchUserProperty,
  };
});
